import type { EnterHttpClient } from '../../http/types';
import type { ApiEnvelope } from '../../types';
import type { PublicWorkspaceAssetCard } from '../../types';
import { partitionCoverKeysByCache, setCachedCoverUrl } from './cover-url-cache';

type PublicResourceUrlData = { urls: Record<string, string> };

export function shouldResolvePublicResource(value?: string | null): value is string {
    return Boolean(value && !/^(https?:|data:|blob:|\/)/i.test(value));
}

export function createPublicResourceResolver(noAuthHttp: EnterHttpClient) {
    const getDownloadPublicResourcePresignedURLs = async (
        resources: string[]
    ): Promise<ApiEnvelope<PublicResourceUrlData>> => {
        const { cached, missing } = partitionCoverKeysByCache(resources);

        if (missing.length === 0) {
            return { code: 0, message: 'ok', data: { urls: cached } };
        }

        const result = await noAuthHttp.post<ApiEnvelope<PublicResourceUrlData>>('/resources', {
            resources: missing,
        });

        if (result.code === 0 && result.data?.urls) {
            const fetchedUrls = result.data.urls;
            for (const [key, url] of Object.entries(fetchedUrls)) {
                setCachedCoverUrl(key, url);
            }
            result.data.urls = { ...cached, ...fetchedUrls };
        }

        return result;
    };

    const resolvePublicResourceUrl = async (
        resource?: string | null
    ): Promise<string | undefined> => {
        if (!resource || !shouldResolvePublicResource(resource)) {
            return resource ?? undefined;
        }

        try {
            const urls = await getDownloadPublicResourcePresignedURLs([resource]);
            return urls.code === 0 ? urls.data?.urls?.[resource] : undefined;
        } catch (error) {
            console.error('Failed to resolve public resource URL:', error);
            return undefined;
        }
    };

    const hydratePublicWorkspaceCardCovers = async <T extends PublicWorkspaceAssetCard>(
        cards: T[]
    ): Promise<T[]> => {
        const coverResources = Array.from(
            new Set(
                cards
                    .map((card) => card.template?.cover || card.cover)
                    .filter(shouldResolvePublicResource)
            )
        );

        if (coverResources.length === 0) {
            return cards.map((card) => {
                const directCover = card.template?.cover || card.cover;
                return directCover && !card.cover_url ? { ...card, cover_url: directCover } : card;
            });
        }

        try {
            const urls = await getDownloadPublicResourcePresignedURLs(coverResources);
            const urlsMap = urls.code === 0 ? (urls.data?.urls ?? {}) : {};

            return cards.map((card) => {
                const rawCover = card.template?.cover || card.cover;
                if (!rawCover || card.cover_url) return card;
                if (shouldResolvePublicResource(rawCover) && !urlsMap[rawCover]) return card;

                return {
                    ...card,
                    cover_url: shouldResolvePublicResource(rawCover) ? urlsMap[rawCover] : rawCover,
                };
            });
        } catch (error) {
            console.error('Failed to resolve public workspace covers:', error);
            return cards;
        }
    };

    return {
        getDownloadPublicResourcePresignedURLs,
        hydratePublicWorkspaceCardCovers,
        resolvePublicResourceUrl,
    };
}
